import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = {
  percentage: 78,
  valid: 156,
  expiring: 23,
  expired: 12,
  missing: 9,
  total: 200,
  projectCount: 5,
};

export function ComplianceOverviewTile({ filter, useMockData }) {
  const { company, userProfile } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Load data
  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id || !userProfile?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const { data: dashData, error } = await supabase.rpc('get_dashboard_data_v2', {
          p_company_id: company.id,
          p_user_id: userProfile.id,
          p_include_archived: false,
          p_limit: 100,
          p_offset: 0,
          p_search: null,
        });

        if (error) throw error;

        let projects = dashData?.projects || [];
        if (filter?.spaceId) {
          projects = projects.filter(p => p.id === filter.spaceId);
        }

        let totalValid = 0;
        let totalExpiring = 0;
        let totalExpired = 0;
        let totalMissing = 0;

        projects.forEach(p => {
          const summary = p.compliance_summary || {};
          totalValid += summary.valid || 0;
          totalExpiring += summary.expiring || 0;
          totalExpired += summary.expired || 0;
          totalMissing += summary.missing || 0;
        });

        const total = totalValid + totalExpiring + totalExpired + totalMissing;
        const percentage = total > 0 ? Math.round((totalValid / total) * 100) : 0;

        setData({
          percentage,
          valid: totalValid,
          expiring: totalExpiring,
          expired: totalExpired,
          missing: totalMissing,
          total,
          projectCount: projects.length,
        });
      } catch (err) {
        console.error('Failed to load compliance data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, userProfile?.id, filter?.spaceId, useMockData]);

  // Determine layout based on dimensions
  const { width, height } = dimensions;
  const isXS = width < 150 || height < 120;
  const isSmall = width < 250 && height < 200;
  const isTall = height > width * 1.3;
  const isWide = width > height * 1.5;
  const isLarge = width >= 350 && height >= 300;

  const getStatusColor = (pct) => {
    if (pct >= 90) return 'text-green-600';
    if (pct >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getProgressColor = (pct) => {
    if (pct >= 90) return 'bg-green-500';
    if (pct >= 70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getBgColor = (pct) => {
    if (pct >= 90) return 'bg-green-50';
    if (pct >= 70) return 'bg-amber-50';
    return 'bg-red-50';
  };

  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full">
        <div className="animate-pulse">
          <div className="w-16 h-16 rounded-full bg-gray-200" />
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full text-gray-400">
        <span className="text-sm">No data</span>
      </div>
    );
  }

  // XS Layout: Just the number
  if (isXS) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full">
        <div className={cn('text-3xl font-bold', getStatusColor(data.percentage))}>
          {data.percentage}%
        </div>
      </div>
    );
  }

  // Small Layout: Number + label + mini progress
  if (isSmall) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full p-2">
        <div className={cn('text-4xl font-bold', getStatusColor(data.percentage))}>
          {data.percentage}%
        </div>
        <p className="text-xs text-gray-500 mt-1">Compliance</p>
        <div className="w-full mt-2">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={cn('h-full', getProgressColor(data.percentage))}
              style={{ width: `${data.percentage}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Tall Layout: Vertical stack
  if (isTall) {
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2">
        {/* Main percentage */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className={cn('text-5xl font-bold', getStatusColor(data.percentage))}>
              {data.percentage}%
            </div>
            <p className="text-sm text-gray-500 mt-1">Overall Compliance</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
          <div
            className={cn('h-full', getProgressColor(data.percentage))}
            style={{ width: `${data.percentage}%` }}
          />
        </div>

        {/* Vertical stats */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between bg-green-50 rounded px-2 py-1">
            <span className="text-xs text-green-600">Valid</span>
            <span className="text-sm font-semibold text-green-700">{data.valid}</span>
          </div>
          <div className="flex items-center justify-between bg-amber-50 rounded px-2 py-1">
            <span className="text-xs text-amber-600">Expiring</span>
            <span className="text-sm font-semibold text-amber-700">{data.expiring}</span>
          </div>
          <div className="flex items-center justify-between bg-red-50 rounded px-2 py-1">
            <span className="text-xs text-red-600">Expired</span>
            <span className="text-sm font-semibold text-red-700">{data.expired}</span>
          </div>
          <div className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
            <span className="text-xs text-gray-600">Missing</span>
            <span className="text-sm font-semibold text-gray-700">{data.missing}</span>
          </div>
        </div>
      </div>
    );
  }

  // Wide Layout: Horizontal arrangement
  if (isWide && !isLarge) {
    return (
      <div ref={containerRef} className="flex items-center h-full gap-4 p-2">
        {/* Left: Percentage */}
        <div className="flex-shrink-0 text-center">
          <div className={cn('text-5xl font-bold', getStatusColor(data.percentage))}>
            {data.percentage}%
          </div>
          <p className="text-xs text-gray-500">Compliance</p>
        </div>

        {/* Right: Stats */}
        <div className="flex-1 grid grid-cols-2 gap-1.5">
          <div className="bg-green-50 rounded p-1.5 text-center">
            <div className="text-lg font-semibold text-green-700">{data.valid}</div>
            <div className="text-xs text-green-600">Valid</div>
          </div>
          <div className="bg-amber-50 rounded p-1.5 text-center">
            <div className="text-lg font-semibold text-amber-700">{data.expiring}</div>
            <div className="text-xs text-amber-600">Expiring</div>
          </div>
          <div className="bg-red-50 rounded p-1.5 text-center">
            <div className="text-lg font-semibold text-red-700">{data.expired}</div>
            <div className="text-xs text-red-600">Expired</div>
          </div>
          <div className="bg-gray-50 rounded p-1.5 text-center">
            <div className="text-lg font-semibold text-gray-700">{data.missing}</div>
            <div className="text-xs text-gray-600">Missing</div>
          </div>
        </div>
      </div>
    );
  }

  // Large Layout: Full featured
  if (isLarge) {
    return (
      <div ref={containerRef} className="flex flex-col h-full p-3">
        {/* Top: Large percentage with ring indicator */}
        <div className="flex-1 flex items-center justify-center">
          <div className="relative">
            {/* Background ring */}
            <svg className="w-32 h-32 transform -rotate-90">
              <circle
                cx="64"
                cy="64"
                r="56"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="12"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                fill="none"
                className={data.percentage >= 90 ? 'stroke-green-500' : data.percentage >= 70 ? 'stroke-amber-500' : 'stroke-red-500'}
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={`${(data.percentage / 100) * 352} 352`}
              />
            </svg>
            {/* Center text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className={cn('text-3xl font-bold', getStatusColor(data.percentage))}>
                  {data.percentage}%
                </div>
                <p className="text-xs text-gray-500">Compliance</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: Stats grid */}
        <div className="grid grid-cols-4 gap-2 mt-4">
          <div className="bg-green-50 rounded-lg p-2 text-center">
            <div className="text-xl font-bold text-green-700">{data.valid}</div>
            <div className="text-xs text-green-600">Valid</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-2 text-center">
            <div className="text-xl font-bold text-amber-700">{data.expiring}</div>
            <div className="text-xs text-amber-600">Expiring</div>
          </div>
          <div className="bg-red-50 rounded-lg p-2 text-center">
            <div className="text-xl font-bold text-red-700">{data.expired}</div>
            <div className="text-xs text-red-600">Expired</div>
          </div>
          <div className="bg-gray-100 rounded-lg p-2 text-center">
            <div className="text-xl font-bold text-gray-700">{data.missing}</div>
            <div className="text-xs text-gray-600">Missing</div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-xs text-gray-400 text-center mt-3">
          Across {data.projectCount} space{data.projectCount !== 1 ? 's' : ''} • {data.total} requirements
        </p>
      </div>
    );
  }

  // Default/Medium Layout
  return (
    <div ref={containerRef} className="flex flex-col h-full p-2">
      {/* Main percentage display */}
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className={cn('text-5xl font-bold', getStatusColor(data.percentage))}>
            {data.percentage}%
          </div>
          <p className="text-sm text-gray-500 mt-1">Overall Compliance</p>
          <p className="text-xs text-gray-400">{data.projectCount} space{data.projectCount !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={cn('h-full', getProgressColor(data.percentage))}
          style={{ width: `${data.percentage}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-1.5 mt-3">
        <div className="bg-green-50 rounded p-1 text-center">
          <div className="text-sm font-semibold text-green-700">{data.valid}</div>
          <div className="text-[10px] text-green-600">Valid</div>
        </div>
        <div className="bg-amber-50 rounded p-1 text-center">
          <div className="text-sm font-semibold text-amber-700">{data.expiring}</div>
          <div className="text-[10px] text-amber-600">Expiring</div>
        </div>
        <div className="bg-red-50 rounded p-1 text-center">
          <div className="text-sm font-semibold text-red-700">{data.expired}</div>
          <div className="text-[10px] text-red-600">Expired</div>
        </div>
        <div className="bg-gray-50 rounded p-1 text-center">
          <div className="text-sm font-semibold text-gray-700">{data.missing}</div>
          <div className="text-[10px] text-gray-600">Missing</div>
        </div>
      </div>
    </div>
  );
}
